import { useEffect, useState } from "react"

// ─────────────────────────────────────────────────────────────────────────────
// PRICING ACCESS
//
// The price is not emailed and no document is generated. Giving a name, a
// company and a work email reveals the figure IN PLACE, and the visitor is
// remembered so it stays revealed on the next visit.
//
// Two things happen on submit, in this order:
//   1. the details go to the same CRM endpoint the Contact Us form posts to
//   2. a first-party cookie is set, and every gate on the page unlocks at once
//
// The unlock is site-wide, not per offer. Someone who has identified themselves
// once should not be asked again on the next offer they open.
// ─────────────────────────────────────────────────────────────────────────────

const COOKIE = "vm_pricing_access"
const MAX_AGE = 60 * 60 * 24 * 180 // 180 days
const EVENT = "vm:pricing-access"

// Same endpoint and the same payload shape as src/pages/contact.js. Reusing
// postFormData from services/contact.js was rejected: it fires antd toasts
// ("Mail Sent Successfully.") on completion, and this form reports through its
// own modal — two success messages for one action.
const CRM_URL = "https://staging.visiomate.com/crmPipeline"
const RECAPTCHA_URL = "https://staging.visiomate.com/verify-recaptcha-site"
const EMAIL_TO = "info@visiomate.com"

const isBrowser = () => typeof window !== "undefined" && typeof document !== "undefined"

/** @returns {{name: string} | null} */
export const readAccess = () => {
  if (!isBrowser()) return null
  const hit = document.cookie
    .split("; ")
    .find(row => row.startsWith(COOKIE + "="))
  if (!hit) return null
  try {
    const parsed = JSON.parse(decodeURIComponent(hit.slice(COOKIE.length + 1)))
    return parsed && typeof parsed.name === "string" ? parsed : { name: "" }
  } catch {
    // A malformed cookie still means they identified themselves once.
    return { name: "" }
  }
}

const writeAccess = name => {
  if (!isBrowser()) return
  // Only the given name is stored — never the email or the company. It exists
  // so the page can greet them; nothing here needs to identify them again.
  const value = encodeURIComponent(JSON.stringify({ name: name || "" }))
  const secure = window.location.protocol === "https:" ? "; Secure" : ""
  document.cookie = `${COOKIE}=${value}; Max-Age=${MAX_AGE}; Path=/; SameSite=Lax${secure}`
  window.dispatchEvent(new CustomEvent(EVENT))
}

/**
 * Every gate and every price on the page reads this.
 *
 * Starts locked on purpose. Gatsby serves static HTML with no knowledge of the
 * cookie, so returning the unlocked state on the first render would be a
 * hydration mismatch — React would discard the server markup. The cookie is
 * read after mount instead, and the reveal is a re-render.
 */
export const usePricingUnlocked = () => {
  const [access, setAccess] = useState(null)

  useEffect(() => {
    const sync = () => setAccess(readAccess())
    sync()
    window.addEventListener(EVENT, sync)
    // Another tab may have unlocked in the meantime.
    window.addEventListener("focus", sync)
    return () => {
      window.removeEventListener(EVENT, sync)
      window.removeEventListener("focus", sync)
    }
  }, [])

  return { unlocked: !!access, name: (access && access.name) || "" }
}

/**
 * Send the details, then unlock.
 *
 * The unlock is deliberately NOT conditional on the CRM call succeeding. The
 * visitor has done what was asked of them; holding the price back because a
 * third-party endpoint is down would punish them for our outage. A failure is
 * logged and reported by the caller, and the lead is the thing that is lost —
 * not the visitor's access.
 */
export const submitPricingRequest = async ({
  name,
  company,
  email,
  offerSlug,
  offerName,
  sourcePath,
  recaptchaToken,
}) => {
  const description = [
    `Company: ${company}`,
    `Offer: ${offerName} (${offerSlug})`,
    `Requested from: ${sourcePath}`,
    "",
    "Asked to see the price and scope for this offer.",
  ].join("\n")

  const payload = {
    name: `Pricing request: ${company || name}`,
    contact_name: name,
    email_from: email,
    phone: "",
    message: `Pricing request for ${offerName}`,
    description,
    email_to: EMAIL_TO,
    attachment_base64: "",
  }

  let delivered = false
  try {
    // Same verification the contact form does. The site already loads
    // reCAPTCHA v3 app-wide via gatsby-browser.js, so this costs no new
    // dependency and no new script.
    if (recaptchaToken) {
      const check = await fetch(RECAPTCHA_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: recaptchaToken }),
      })
      const verdict = await check.json()
      if (!(verdict.success && verdict.score > 0.5)) {
        throw new Error("recaptcha")
      }
    }

    const res = await fetch(CRM_URL, {
      method: "POST",
      headers: { "Content-type": "application/json; charset=UTF-8" },
      body: JSON.stringify(payload),
    })
    const body = await res.json()
    delivered =
      body.statusCode === 200 || (body.result && body.result.status === true)
  } catch (e) {
    delivered = false
  }

  writeAccess((name || "").trim().split(/\s+/)[0])
  return { unlocked: true, delivered }
}

export default submitPricingRequest
